import { useState } from "react";
import { Plus, Trash2, Clock, AlertCircle, CheckCircle } from "lucide-react";
import { toast } from "react-toastify";

const StaffWorkTimeTab = ({ formData, setFormData }) => {
  const [confirmDelete, setConfirmDelete] = useState(null); // {day, index}

  // تولید لیست ساعت‌های نیم‌ساعتی از 07:00 تا 23:30
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 7; hour <= 23; hour++) {
      slots.push(`${hour.toString().padStart(2, "0")}:00`);
      if (hour < 23) {
        slots.push(`${hour.toString().padStart(2, "0")}:30`);
      }
    }
    slots.push("23:30");
    return slots;
  };

  const allTimeSlots = generateTimeSlots();

  const daysOfWeek = [
    { key: "shanbeh", label: "شنبه", emoji: "📅" },
    { key: "yekshanbeh", label: "یکشنبه", emoji: "📅" },
    { key: "doshanbeh", label: "دوشنبه", emoji: "📅" },
    { key: "seshanbeh", label: "سه‌شنبه", emoji: "📅" },
    { key: "chaharshanbeh", label: "چهارشنبه", emoji: "📅" },
    { key: "panjshanbeh", label: "پنجشنبه", emoji: "📅" },
    { key: "jomeh", label: "جمعه", emoji: "📅" },
  ];

  // دریافت ساعت‌های موجود برای انتخاب (حذف ساعت‌های قبلاً انتخاب شده)
  const getAvailableSlots = (day) => {
    const selectedSlots = formData.workTime[day] || [];
    return allTimeSlots.filter((slot) => !selectedSlots.includes(slot));
  };

  // افزودن ساعت جدید
  const handleAddTime = (day) => {
    const availableSlots = getAvailableSlots(day);

    if (availableSlots.length === 0) {
      toast.warning("تمام ساعت‌های این روز انتخاب شده است", {
        className: "z-[10001]",
      });
      return;
    }

    // افزودن اولین ساعت موجود به عنوان پیش‌فرض
    const newSlot = availableSlots[0];

    setFormData((prev) => ({
      ...prev,
      workTime: {
        ...prev.workTime,
        [day]: [...(prev.workTime[day] || []), newSlot],
      },
    }));

    toast.success(`ساعت ${newSlot} به ${daysOfWeek.find((d) => d.key === day)?.label} اضافه شد`, {
      className: "z-[10001]",
    });
  };

  // تغییر ساعت انتخابی
  const handleTimeChange = (day, index, newValue) => {
    const currentSlots = [...(formData.workTime[day] || [])];

    // بررسی تکراری نبودن
    if (currentSlots.includes(newValue) && currentSlots[index] !== newValue) {
      toast.error("این ساعت قبلاً انتخاب شده است", {
        className: "z-[10001]",
      });
      return;
    }

    currentSlots[index] = newValue;

    setFormData((prev) => ({
      ...prev,
      workTime: {
        ...prev.workTime,
        [day]: currentSlots,
      },
    }));
  };

  // حذف ساعت با تایید
  const handleDeleteTime = (day, index) => {
    setConfirmDelete({ day, index });
  };

  const confirmDeleteAction = () => {
    if (!confirmDelete) return;

    const { day, index } = confirmDelete;
    const currentSlots = [...(formData.workTime[day] || [])];
    const deletedSlot = currentSlots[index];

    currentSlots.splice(index, 1);

    setFormData((prev) => ({
      ...prev,
      workTime: {
        ...prev.workTime,
        [day]: currentSlots,
      },
    }));

    toast.success(`ساعت ${deletedSlot} حذف شد`, {
      className: "z-[10001]",
    });

    setConfirmDelete(null);
  };

  const cancelDelete = () => {
    setConfirmDelete(null);
  };

  return (
    <div className="space-y-6">
      {/* راهنما */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">راهنمای تنظیم ساعت کاری:</p>
          <ul className="list-disc list-inside space-y-1 text-blue-700">
            <li>برای هر روز می‌توانید چندین بازه زمانی تعریف کنید</li>
            <li>ساعت‌ها به صورت نیم‌ساعتی از 07:00 تا 23:30 قابل انتخاب هستند</li>
            <li>اگر روزی خالی باشد، کارمند در آن روز کار نمی‌کند</li>
          </ul>
        </div>
      </div>

      {/* لیست روزهای هفته */}
      <div className="space-y-4">
        {daysOfWeek.map((day) => {
          const daySlots = formData.workTime[day.key] || [];
          const availableSlots = getAvailableSlots(day.key);

          return (
            <div
              key={day.key}
              className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow duration-300"
            >
              {/* هدر روز */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-md shadow-purple-500/25">
                    <span className="text-xl">{day.emoji}</span>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-800">
                      {day.label}
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {daySlots.length === 0
                        ? <span className="text-red-600">تعطیل</span>
                        : `${daySlots.length} تایم ثبت شده`}
                    </p>
                  </div>
                </div>

                {/* دکمه افزودن */}
                <button
                  onClick={() => handleAddTime(day.key)}
                  disabled={availableSlots.length === 0}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 rounded-lg shadow-md shadow-green-500/25 hover:shadow-lg hover:shadow-green-500/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">افزودن ساعت</span>
                </button>
              </div>

              {/* لیست ساعت‌های انتخاب شده */}
              {daySlots.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {daySlots.map((slot, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3 group hover:border-purple-300 transition-colors duration-300"
                    >
                      {/* Select ساعت */}
                      <select
                        value={slot}
                        onChange={(e) =>
                          handleTimeChange(day.key, index, e.target.value)
                        }
                        className="flex-1 px-3 py-2 text-sm font-mono bg-white border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:border-purple-400 transition-colors duration-300"
                        dir="ltr"
                      >
                        <option value={slot}>{slot}</option>
                        {availableSlots.map((availableSlot) => (
                          <option key={availableSlot} value={availableSlot}>
                            {availableSlot}
                          </option>
                        ))}
                      </select>

                      {/* دکمه حذف */}
                      <button
                        onClick={() => handleDeleteTime(day.key, index)}
                        className="w-9 h-9 flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-300 group-hover:opacity-100 opacity-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400 text-sm">
                  <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>هنوز ساعتی برای این روز تعریف نشده است</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* مودال تایید حذف */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10000] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-300">
            {/* آیکون هشدار */}
            <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-7 h-7 text-red-600" />
            </div>

            {/* متن */}
            <h3 className="text-lg font-bold text-slate-800 text-center mb-2">
              حذف ساعت کاری
            </h3>
            <p className="text-sm text-slate-600 text-center mb-6">
              آیا از حذف ساعت{" "}
              <span className="font-bold text-slate-800 font-mono">
                {formData.workTime[confirmDelete.day]?.[confirmDelete.index]}
              </span>{" "}
              از روز{" "}
              <span className="font-bold text-slate-800">
                {
                  daysOfWeek.find((d) => d.key === confirmDelete.day)?.label
                }
              </span>{" "}
              اطمینان دارید؟
            </p>

            {/* دکمه‌ها */}
            <div className="flex items-center gap-3">
              <button
                onClick={cancelDelete}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors duration-300"
              >
                انصراف
              </button>
              <button
                onClick={confirmDeleteAction}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-lg shadow-md shadow-red-500/25 hover:shadow-lg hover:shadow-red-500/30 transition-all duration-300"
              >
                حذف کن
              </button>
            </div>
          </div>
        </div>
      )}

      {/* خلاصه ساعت‌های کاری */}
      <div className="bg-gradient-to-br from-slate-50 to-purple-50/30 border border-slate-200 rounded-lg p-5">
        <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-green-600" />
          خلاصه ساعت‌های کاری هفته
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
          {daysOfWeek.map((day) => {
            const daySlots = formData.workTime[day.key] || [];
            return (
              <div
                key={day.key}
                className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-slate-200"
              >
                <span className="text-slate-600 font-medium">{day.label}:</span>
                <span
                  className={`font-mono text-xs ${
                    daySlots.length > 0
                      ? "text-green-600 font-semibold"
                      : "text-red-500"
                  }`}
                >
                  {daySlots.length > 0
                    ? `${daySlots.length} تایم`
                    : "تعطیل"}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StaffWorkTimeTab;